import { useQuery } from '@tanstack/react-query'
import {
  BoosterProgramItem,
  SearchBoosterProgramApiParams,
  SearchBoosterProgramParams,
} from 'Modules'

import { AppAdmin } from 'api/app-admin'
import {
  mapApiToBoosterProgramItem,
  mapStatusToApiCode,
  mapTargetUsersToApi,
} from 'modules/booster-program/api-adapters'
import { logError } from 'utils/logger'

import { boosterProgramKeys } from './query-keys'

interface BoosterProgramListResult {
  results: BoosterProgramItem[]
  total: number
}

const fetchBoosterPrograms = async (
  params: SearchBoosterProgramParams
): Promise<BoosterProgramListResult> => {
  const apiParams: SearchBoosterProgramApiParams = {
    page: params.page,
    limit: params.limit,
    title: params.keyword,
    status: mapStatusToApiCode(params.status),
    targetUser: mapTargetUsersToApi(params.targetUsers),
    sort: params.sort,
  }

  try {
    const res = await AppAdmin.getBoosterPrograms(apiParams)
    if (!res.data?.success) {
      return { results: [], total: 0 }
    }
    const payload = res.data.data
    const results = (payload?.results ?? []).map(mapApiToBoosterProgramItem)
    return {
      results,
      total: payload?.totalResults ?? results.length,
    }
  } catch (error) {
    logError('Error fetching booster programs:', error)
    return { results: [], total: 0 }
  }
}

export const useBoosterPrograms = (params: SearchBoosterProgramParams) => {
  const query = useQuery({
    queryKey: boosterProgramKeys.list(params),
    queryFn: () => fetchBoosterPrograms(params),
    gcTime: 30000,
  })

  return {
    programs: query.data?.results ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
  }
}
